import React, { useState, useEffect, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import {
    Col,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Form,
    FormGroup,
    Label,
    Row,
    Card,
    CardHeader,
    CardBody,
    Spinner,
    Alert,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { generateInventoryReport } from '../Helpers/Api';
import { getRequestTypes } from '../../RMVendors/Helpers/Api';

const GenerateInventoryReport = ({ propertyId, buildingName, toggle, modal, options, user }) => {
    const [requestTypes, setRequestTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? '#007bff' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
            '&:hover': {
                borderColor: '#007bff',
            },
            minHeight: '38px',
            fontSize: '14px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#007bff'
                : state.isFocused
                    ? '#f8f9fa'
                    : 'white',
            color: state.isSelected ? 'white' : '#495057',
            '&:hover': {
                backgroundColor: state.isSelected ? '#007bff' : '#e9ecef',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#6c757d',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#495057',
        }),
    };

    const fetchRequestTypes = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getRequestTypes(user?.company_id || user?.id || '');
            setRequestTypes(data);
        } catch (error) {
            setError(error.message);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message,
                confirmButtonColor: '#007bff'
            });
        } finally {
            setLoading(false);
        }
    }, [user?.company_id, user?.id]);

    useEffect(() => {
        if (modal) {
            fetchRequestTypes();
        }
    }, [modal, fetchRequestTypes]);

    const formik = useFormik({
        initialValues: {
            itemName: '',
            modelNo: '',
            location: '',
            status: '',
            requestsSupported: '',
        },
        validationSchema: Yup.object({
            itemName: Yup.string().optional(),
            modelNo: Yup.string().optional(),
            location: Yup.string().optional(),
            status: Yup.string().optional(),
            requestsSupported: Yup.string().optional(),
        }),
        onSubmit: async (values) => {
            const result = await Swal.fire({
                title: 'Generate Inventory Report',
                text: 'Do you want to generate the report with these filters?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, Generate Report',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#007bff',
                cancelButtonColor: '#6c757d',
                customClass: {
                    popup: 'swal2-popup-custom',
                    title: 'swal2-title-custom',
                }
            });

            if (result.isConfirmed) {
                try {
                    setLoading(true);
                    const response = await generateInventoryReport(
                        {
                            property_id: propertyId,
                            building_name: buildingName,
                            ...values,
                        },
                        {
                            responseType: 'blob',
                        }
                    );

                    // Verify the content type from the response headers
                    const contentType = response.type || 'application/octet-stream';
                    if (!contentType.includes('vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
                        throw new Error('Invalid file format received from server');
                    }

                    const blob = new Blob([response], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });

                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `InventoryReport_${buildingName.replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    Swal.fire({
                        title: 'Success!',
                        text: 'Excel report downloaded successfully.',
                        icon: 'success',
                        confirmButtonColor: '#28a745'
                    });
                    toggle();
                } catch (error) {
                    Swal.fire({
                        title: 'Error!',
                        text: error.message || 'Failed to generate report. Please ensure the server is configured correctly.',
                        icon: 'error',
                        confirmButtonColor: '#dc3545'
                    });
                } finally {
                    setLoading(false);
                }
            }
        },
    });

    const formatOptionsForSelect = (optionsArray) => {
        return optionsArray?.map(option => ({
            value: option,
            label: option
        })) || [];
    };

    const formatRequestTypesForSelect = (requestTypesArray) => {
        return requestTypesArray?.map(type => ({
            value: type._id,
            label: type.name
        })) || [];
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        formik.setFieldValue(fieldName, selectedOption ? selectedOption.value : '');
    };

    const getSelectValue = (fieldName, optionsArray) => {
        const value = formik.values[fieldName];
        if (!value) return null;

        if (fieldName === 'requestsSupported') {
            return formatRequestTypesForSelect(requestTypes).find(option => option.value === value);
        }
        return formatOptionsForSelect(optionsArray).find(option => option.value === value);
    };

    return (
        <Modal isOpen={modal} toggle={toggle} size="xl" className="generate-report-modal">
            <ModalHeader toggle={toggle} className="border-bottom">
                <div className="d-flex align-items-center">
                    <i className="fas fa-chart-line me-2 text-primary"></i>
                    <span>Generate Inventory Report</span>
                </div>
            </ModalHeader>

            <Form onSubmit={formik.handleSubmit}>
                <ModalBody className="p-4">
                    {error && (
                        <Alert color="danger" className="mb-4">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                    )}

                    <Card className="border-0 shadow-sm">
                        <CardHeader className="bg-light border-bottom">
                            <h6 className="mb-0 text-dark">
                                <i className="fas fa-filter me-2"></i>
                                Report Filters
                            </h6>
                            <small className="text-muted">
                                Select criteria to filter your report. Leave blank to include all items.
                            </small>
                        </CardHeader>
                        <CardBody className="p-4">
                            <Row className="mb-3">
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="form-label fw-semibold">
                                            <i className="fas fa-cube me-2 text-primary"></i>
                                            Item Name
                                        </Label>
                                        <Select
                                            name="itemName"
                                            options={formatOptionsForSelect(options?.itemNames)}
                                            value={getSelectValue('itemName', options?.itemNames)}
                                            onChange={(selectedOption) => handleSelectChange(selectedOption, 'itemName')}
                                            onBlur={() => formik.setFieldTouched('itemName', true)}
                                            placeholder="Select Item Name..."
                                            isClearable
                                            isSearchable
                                            styles={selectStyles}
                                            className="react-select-container mb-3"
                                            classNamePrefix="react-select"
                                        />
                                        {formik.touched.itemName && formik.errors.itemName && (
                                            <div className="text-danger small mt-1">
                                                <i className="fas fa-exclamation-circle me-1"></i>
                                                {formik.errors.itemName}
                                            </div>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="form-label fw-semibold">
                                            <i className="fas fa-tag me-2 text-primary"></i>
                                            Model Number
                                        </Label>
                                        <Select
                                            name="modelNo"
                                            options={formatOptionsForSelect(options?.modelNos)}
                                            value={getSelectValue('modelNo', options?.modelNos)}
                                            onChange={(selectedOption) => handleSelectChange(selectedOption, 'modelNo')}
                                            onBlur={() => formik.setFieldTouched('modelNo', true)}
                                            placeholder="Select Model Number..."
                                            isClearable
                                            isSearchable
                                            styles={selectStyles}
                                            className="react-select-container mb-3"
                                            classNamePrefix="react-select"
                                        />
                                        {formik.touched.modelNo && formik.errors.modelNo && (
                                            <div className="text-danger small mt-1">
                                                <i className="fas fa-exclamation-circle me-1"></i>
                                                {formik.errors.modelNo}
                                            </div>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label className="form-label fw-semibold">
                                            <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                                            Location
                                        </Label>
                                        <Select
                                            name="location"
                                            options={formatOptionsForSelect(options?.locations)}
                                            value={getSelectValue('location', options?.locations)}
                                            onChange={(selectedOption) => handleSelectChange(selectedOption, 'location')}
                                            onBlur={() => formik.setFieldTouched('location', true)}
                                            placeholder="Select Location..."
                                            isClearable
                                            isSearchable
                                            styles={selectStyles}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                        {formik.touched.location && formik.errors.location && (
                                            <div className="text-danger small mt-1">
                                                <i className="fas fa-exclamation-circle me-1"></i>
                                                {formik.errors.location}
                                            </div>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label className="form-label fw-semibold">
                                            <i className="fas fa-info-circle me-2 text-primary"></i>
                                            Status
                                        </Label>
                                        <Select
                                            name="status"
                                            options={formatOptionsForSelect(options?.statuses)}
                                            value={getSelectValue('status', options?.statuses)}
                                            onChange={(selectedOption) => handleSelectChange(selectedOption, 'status')}
                                            onBlur={() => formik.setFieldTouched('status', true)}
                                            placeholder="Select Status..."
                                            isClearable
                                            isSearchable
                                            styles={selectStyles}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                        {formik.touched.status && formik.errors.status && (
                                            <div className="text-danger small mt-1">
                                                <i className="fas fa-exclamation-circle me-1"></i>
                                                {formik.errors.status}
                                            </div>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label className="form-label fw-semibold">
                                            <i className="fas fa-tasks me-2 text-primary"></i>
                                            Requests Supported
                                        </Label>
                                        <Select
                                            name="requestsSupported"
                                            options={formatRequestTypesForSelect(requestTypes)}
                                            value={getSelectValue('requestsSupported', requestTypes)}
                                            onChange={(selectedOption) => handleSelectChange(selectedOption, 'requestsSupported')}
                                            onBlur={() => formik.setFieldTouched('requestsSupported', true)}
                                            placeholder="Select Requests Supported..."
                                            isClearable
                                            isSearchable
                                            styles={selectStyles}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            isLoading={loading && requestTypes.length === 0}
                                        />
                                        {formik.touched.requestsSupported && formik.errors.requestsSupported && (
                                            <div className="text-danger small mt-1">
                                                <i className="fas fa-exclamation-circle me-1"></i>
                                                {formik.errors.requestsSupported}
                                            </div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    <div className="mt-3 p-3 bg-light rounded">
                        <div className="d-flex align-items-center">
                            <i className="fas fa-building me-2 text-muted"></i>
                            <span className="fw-semibold text-dark">Building:</span>
                            <span className="ms-2 text-primary">{buildingName}</span>
                        </div>
                    </div>
                </ModalBody>

                <ModalFooter className="border-top bg-light">
                    <Button
                        color="primary"
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2"
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-download me-2"></i>
                                Generate Report
                            </>
                        )}
                    </Button>
                    <Button
                        color="secondary"
                        onClick={toggle}
                        disabled={loading}
                        className="px-4 py-2"
                    >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                    </Button>
                </ModalFooter>
            </Form>

            <style jsx>{`
                .generate-report-modal .modal-header {
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                }
                
                .react-select-container .react-select__control {
                    border-radius: 0.375rem;
                    transition: all 0.15s ease-in-out;
                }
                
                .react-select-container .react-select__control:hover {
                    border-color: #007bff;
                }
                
                .react-select-container .react-select__menu {
                    border-radius: 0.375rem;
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
                }
                
                .swal2-popup-custom {
                    border-radius: 0.5rem;
                }
                
                .swal2-title-custom {
                    color: #495057;
                }
                
                .form-label {
                    margin-bottom: 0.5rem;
                    color: #495057;
                }
                
                .card {
                    transition: box-shadow 0.15s ease-in-out;
                }
                
                .card:hover {
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
                }
            `}</style>
        </Modal>
    );
};

export { GenerateInventoryReport };